





























































































































































































































































































import moment from 'moment';

import { mapActions, mapGetters } from 'vuex';
import { ErrorManager } from '@/models/error';

import Functions from '@/mixins/functions';
import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import Snackbar from '@/mixins/snackbar';

import {
  IEmployee,
  IOvertimeRequest,
  IOvertimeRequestData,
  IUserData,
  OvertimeRequest,
  OvertimeRequestStateEnum
} from '@/models';
import { merge, omit } from 'lodash';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import OvertimeRequestFormDialog from '@/components/overtime-request/OvertimeRequestFormDialog.vue';
import OvertimeRequestDetailDialog from '@/components/overtime-request/OvertimeRequestDetailDialog.vue';
import OvertimeRequestSearchFilter from '@/components/overtime-request/OvertimeRequestSearchFilter.vue';
import OvertimeRequestActivityDialog from '@/components/overtime-request/OvertimeRequestActivityDialog.vue';
import {
  IFetchOvertimeRequestsFilterParams,
  IUpdateOvertimeStatePayload
} from '@/store/overtime-request';

import ALPage from '@/components/core/Page/ALPage.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';

const baseMixins = mixins(Functions, auth, Snackbar);

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    overtimeRequestFormDialog: InstanceType<typeof OvertimeRequestFormDialog>;
    overtimeRequestActivityDialog: InstanceType<
      typeof OvertimeRequestActivityDialog
    >;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    components: {
      ConfirmationDialog,
      ErrorAlert,
      OvertimeRequestFormDialog,
      OvertimeRequestDetailDialog,
      OvertimeRequestSearchFilter,
      OvertimeRequestActivityDialog,
      ALPage
    },
    data() {
      return {
        loading: false,
        detailDialog: false,
        isSearchFilterActive: false,

        selectedOvertime: {} as IOvertimeRequestData,
        params: {
          search: '',
          page: 1
        } as IFetchOvertimeRequestsFilterParams,

        sortBy: 'id',
        sortDesc: true,

        errorMessage: '',
        detailErrorMessage: '',

        headers: [
          {
            text: 'ID',
            sortable: false,
            value: 'id',
            width: 50
          },
          {
            text: 'Employee',
            sortable: false,
            value: 'employee',
            width: 200
          },
          {
            text: 'Date',
            sortable: false,
            value: 'date',
            width: 220
          },
          {
            text: 'Approver',
            sortable: false,
            value: 'approvers',
            width: 200
          },
          {
            text: 'Duration (Hours)',
            value: 'amount_requested',
            align: 'center',
            width: 150
          },
          {
            text: 'Last Modified',
            sortable: false,
            value: 'updated_at',
            width: 200
          },
          {
            text: 'Status',
            sortable: false,
            align: 'center',
            value: 'state',
            width: 200
          },
          {
            text: '',
            sortable: false,
            value: 'actions',
            align: 'center',
            width: 100
          }
        ],

        overtimeDetailQueryParam: this.$route.query.request,

        //comments
        OvertimeRequestStateEnum,
        cancelConfirmationDialog: false,
        commentsDialog: false,

        overtimeComment: '',
        actionLabel: '',

        commentErrorMessage: '',
        commentErrorMessageDetail: '',

        state: OvertimeRequestStateEnum.Draft
      };
    },

    computed: {
      ...mapGetters({
        overtimeRequests: 'overtimeRequest/getOvertimeRequests',
        getOvertimeRequestById: 'overtimeRequest/getOvertimeRequestById',
        numOvertimes: 'overtimeRequest/getOvertimeCount'
      }),

      //convert start and end time to correct utc before usage
      overtimeRequestsInUTC(): IOvertimeRequest[] {
        return this.overtimeRequests.map((requests: IOvertimeRequest) => {
          return new OvertimeRequest({
            ...requests,
            start: moment(requests.start)
              .utc(false)
              .format('YYYY-MM-DDTHH:mm:ss'),
            end: moment(requests.end)
              .utc(false)
              .format('YYYY-MM-DDTHH:mm:ss')
          });
        });
      },

      isOvertimeApprover(): boolean {
        const approverOvertime = this.overtimeRequests.filter(
          (overtime: IOvertimeRequest) => {
            return overtime
              .approvers!.map(approver => (approver.approver as IUserData).id)
              .includes(this.currentUser.id);
          }
        );
        return approverOvertime;
      },

      extraFiltersApplied(): boolean {
        return Object.keys(omit(this.$route.query, ['page', 'search'])).some(
          v => !!v
        );
      }
    },

    watch: {
      'params.search'(search: string) {
        this.debounceFetchOvertimeRequests({
          ...this.params,
          search: search,
          page: 1
        }).then(() => {
          this.params.page = 1;
        });
      }
    },

    async mounted() {
      // update `this.params` from the route query object.
      // omitting `search` and `page` as they are already set in the data vue option
      merge(this.params, omit(this.$route.query, ['page', 'search']));

      this.loading = true;
      this.searchOvertimeRequests(this.params);

      if (this.overtimeDetailQueryParam) {
        try {
          const overtime = await this.tryGetOrFetchOvertimeRequest(
            Number(this.overtimeDetailQueryParam)
          );
          if (overtime) {
            this.openOvertimeDetailDialog(
              new OvertimeRequest({
                ...overtime,
                start: moment(overtime.start)
                  .utc(false)
                  .format('YYYY-MM-DDTHH:mm:ss'),
                end: moment(overtime.end)
                  .utc(false)
                  .format('YYYY-MM-DDTHH:mm:ss')
              })
            );
          }
        } catch (e) {
          this.clearOvertimeDetailQueryParam();
          this.createSnack({
            color: 'error darken-1',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Error retrieving overtime request`,
            btnText: 'Ok',
            timeout: 6000
          });
        }
      }
    },

    methods: {
      moment,

      ...mapActions({
        fetchOvertimeRequests: 'overtimeRequest/fetchOvertimeRequests',
        fetchOvertimeRequest: 'overtimeRequest/fetchOvertimeRequest',
        updateOvertimeRequestState: 'overtimeRequest/updateOvertimeRequestState'
      }),

      openActivitesDialog(request: IOvertimeRequest) {
        this.$refs.overtimeRequestActivityDialog.open(request);
      },

      isUserApprover(request: IOvertimeRequest): boolean {
        return request.approvers!.some(
          approver => (approver.approver as IUserData).id == this.currentUser.id
        );
      },

      canRejectOrApproveState(request: IOvertimeRequest): boolean {
        return (
          this.isUserApprover(request) &&
          request.state == OvertimeRequestStateEnum.Submitted
        );
      },

      // Owner of the personal request can cancel anytime when in submitted state.
      // Once approved, only manager can cancel
      // If user is approver or SA they can cancel when not rejected or cancelled
      canCancel(request: IOvertimeRequest): boolean {
        let canCancel = false;

        if (this.isUserOwner(request)) {
          canCancel = request.state == OvertimeRequestStateEnum.Submitted;
        } else if (
          request.state == OvertimeRequestStateEnum.Approved &&
          (this.isUserApprover(request) || this.isUserHRPersonnel)
        ) {
          canCancel = true;
        }
        return canCancel;
      },

      genDayDiff(request: IOvertimeRequestData): number {
        return moment(moment(request.end).format('YYYY-MM-DD')).diff(
          moment(request.start).format('YYYY-MM-DD'),
          'day'
        );
      },

      // Owner of the personal request can edit anytime when in submitted state.
      canEdit(request: IOvertimeRequestData): boolean {
        return (
          this.isUserOwner(request) &&
          request.state == OvertimeRequestStateEnum.Submitted
        );
      },

      isUserOwner(request: IOvertimeRequestData): boolean {
        return (request.employee as IEmployee).id == this.currentUser.employee;
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
        this.commentErrorMessage = '';
        this.commentErrorMessageDetail = '';
      },

      close() {
        this.detailDialog = false;
      },

      openOvertimeDetailDialog(overtime: IOvertimeRequest) {
        this.selectedOvertime = overtime;
        this.detailDialog = true;
      },

      openOvertimeRequestDialog(overtime?: IOvertimeRequestData) {
        this.$refs.overtimeRequestFormDialog.open(overtime);
      },

      async debounceFetchOvertimeRequests(
        params: IFetchOvertimeRequestsFilterParams
      ) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.searchOvertimeRequests, 200)(params);
      },

      searchOvertimeRequests(params: IFetchOvertimeRequestsFilterParams) {
        /* this will update the URL query based on param values. */
        this.$router
          .replace({
            name: 'overtime-request-list',
            query: params as Record<string, any>
          })
          .catch((e: Error) => e); //catch navigation duplication error

        return this.fetchOvertimeRequests(params)
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
            if (error.response) {
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },

      refreshSearchFilter(params: IFetchOvertimeRequestsFilterParams) {
        this.clearAllErrors();

        this.loading = true;
        this.searchOvertimeRequests({
          page: 1,
          search: this.params.search,
          ...params
        }).then(() => {
          // update params objects
          this.params.state = params.state;
          this.params.page = 1;
        });
      },

      onUpdatePage(newPage: number) {
        if (newPage != this.params.page) {
          this.searchOvertimeRequests({
            ...this.params,
            page: newPage
          }).then(() => {
            this.params.page = newPage;
          });
        }
      },

      openCommentDialog(
        request: IOvertimeRequest,
        state: OvertimeRequestStateEnum
      ) {
        this.selectedOvertime = request;
        this.overtimeComment = ''; //clears comment dialog on open
        this.commentsDialog = true;
        this.actionLabel = OvertimeRequestStateEnum[state];
        this.state = state;
      },

      closeCommentsDialog() {
        this.clearAllErrors();
        this.commentsDialog = false;
      },

      setOvertimeState() {
        this.loading = true;

        const payload = {
          id: this.selectedOvertime.id,
          state: this.state,
          comment: this.overtimeComment
        } as IUpdateOvertimeStatePayload;

        this.updateOvertimeRequestState(payload)
          .then(data => {
            this.close();
            this.closeCommentsDialog();
            const stateInfo = new OvertimeRequest({
              ...(this.selectedOvertime as IOvertimeRequestData),
              state: payload.state
            }).getStateInfo();

            this.createSnack({
              color: `${stateInfo.color} darken-1`,
              type: `${stateInfo.color}`,
              right: true,
              useBtn: false,
              msg: `Lieu Time Submission ${stateInfo.display_name}`,
              btnText: 'Ok',
              timeout: 6000
            });
          })
          .catch(error => {
            if (error.response) {
              if (error.response.data.detail) {
                this.commentErrorMessageDetail = error.response.data.detail;
              }
            }
            this.commentErrorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      async tryGetOrFetchOvertimeRequest(
        overtimeId: number
      ): Promise<IOvertimeRequest | undefined> {
        if (!this.getOvertimeRequestById(overtimeId)) {
          await this.fetchOvertimeRequest(overtimeId);
        }

        return this.getOvertimeRequestById(overtimeId);
      },

      clearOvertimeDetailQueryParam() {
        // clear the timeoff detail query in route
        this.$router
          .replace({
            name: 'overtime-request-list'
          })
          .catch((e: Error) => e);
      }
    }
  });
